import { isArray, isEmpty } from 'lodash';
import {
  appName,
  localStorageConstants,
  locale,
  notificationType,
} from './constants/globals';

import { isDate } from 'date-fns';
import dot from 'dot-object';
import moment from 'moment';
import { NotificationManager } from 'react-notifications';
import { v4 as uuidv4 } from 'uuid';

export const readFromCookie = process.env.REACT_APP_READ_FROM_COOKIE === 'true';
export const isWindowsApp = process.env.REACT_APP_WINDOWS_APP === 'true';
export const showStandardPage = process.env.REACT_APP_SHOW_MENU_DEV === 'true';

export const fetchLocaleFromLocalStorage = () => {
  return localStorage.getItem(localStorageConstants.LOCALE) || '';
};

export const fetchAppNameFromLocalStorage = () => {
  return localStorage.getItem(localStorageConstants.APP_NAME) || '';
};

export const fetchAppVersionFromLocalStorage = () => {
  return localStorage.getItem(localStorageConstants.APP_VERSION) || '';
};

export const formTranslation = () => {
  const applicationName = fetchAppNameFromLocalStorage() || appName.default;
  const languageVersion = fetchLocaleFromLocalStorage() || locale.default;
  return applicationName + '_' + languageVersion;
};

export const changePaginationTextToSerbian = (obj, of) => {
  return '' + obj.from + '-' + obj.to + of + obj.count;
};

export const basicFormValidationHandler = (
  objectForValidation,
  errorTranslations
) => {
  const dotNotationObj = dot(objectForValidation);
  let errorsObject = {};
  Object.entries(dotNotationObj?.separator)?.forEach((entry) => {
    let fieldValue = typeof entry[1] === 'string' ? entry[1]?.trim() : entry[1];
    if (typeof entry[1] === 'boolean') {
      fieldValue = entry[1].toString();
    }
    if (
      !fieldValue ||
      fieldValue === '' ||
      fieldValue?.length === 0 ||
      (isDate(fieldValue) &&
        (!moment(fieldValue).isValid() || fieldValue.getFullYear() < 1900)) ||
      (isArray(fieldValue) && isEmpty(fieldValue))
    ) {
      errorsObject[`${entry[0]}`] = errorTranslations[entry[0]];
    }
  });

  return errorsObject;
};

export const dateIntervalFormValidation = (
  objectForValidation,
  errorTranslations
) => {
  let errorsObject = {};

  const dateFrom = moment(objectForValidation?.dateFrom);
  const dateTo = moment(objectForValidation?.dateTo);

  if (dateFrom && dateTo && errorTranslations && dateFrom.isAfter(dateTo)) {
    errorsObject.dateFrom = errorTranslations['dateInterval'];
  }

  return errorsObject;
};

export const getUpdateValueItem = (list, targetProp, value) => {
  return list?.find((item) => item[targetProp] === value) || null;
};

export const createNotification = (
  type,
  titleMessage,
  descriptionMessage,
  duration
) => {
  const infoDuration = duration || 4000;
  const longDuration = duration || 4200;

  switch (type) {
    case notificationType.info:
      NotificationManager.info(descriptionMessage, titleMessage, infoDuration);
      break;
    case notificationType.success:
      NotificationManager.success(
        descriptionMessage,
        titleMessage,
        infoDuration
      );
      break;
    case notificationType.warning:
      NotificationManager.warning(
        descriptionMessage,
        titleMessage,
        infoDuration
      );
      break;
    case notificationType.error:
      NotificationManager.error(descriptionMessage, titleMessage, longDuration);
      break;
    case notificationType.passwordExpiration:
      NotificationManager.warning(descriptionMessage, titleMessage, 300000);
      break;
    default:
      break;
  }
};

export const hideNotifications = (title) => {
  const passwordNotification = NotificationManager['listNotify']?.find(
    (element) => element?.title === title
  );
  if (passwordNotification) NotificationManager.remove(passwordNotification);
};

export const getUserNameAndSurname = (user) => {
  const firstName = user?.firstName;
  const lastName = user?.lastName;
  return firstName && lastName
    ? removeAdditionalSpaces(firstName) + ' ' + removeAdditionalSpaces(lastName)
    : '';
};

export const checkForUserRole = (roles, targetRoleId) => {
  if (!roles) return false;
  return (
    roles?.filter((role) => parseInt(role.id) === targetRoleId).length !== 0
  );
};

export const mailTrigger = ({ recipient, subject, body }) => {
  const emailBody = body || '';
  window.open(`mailto:${recipient}?subject=${subject}&body=${emailBody}`);
};

export const substringToTargetText = (text, targetText) => {
  return text.substring(0, text.indexOf(targetText));
};

export const substringFromTargetText = (text, targetText) => {
  return text.substring(text.indexOf(targetText) + 1);
};

const thousandSeparator = process.env.REACT_APP_THOUSAND_SEPARATOR;

export const splitThousandsWithCommas = (number) => {
  return number
    ?.toString()
    .replace(
      '/B(?<!\\process.env.REACT_APP_DECIMAL_SEPARATORd*)(?=(d{3})+(?!d))/g',
      thousandSeparator
    );
};

export const findTableName = (enums, metaData) => {
  return getKeys(enums)?.find(
    (item) => enums[item]?.text === metaData?.tableName
  );
};

export function getKeys(entity) {
  return entity && !isEmpty(entity) ? Object.keys(entity) : [];
}

export function findEnumObjectByKey(enums, key) {
  return enums ? enums[getKeys(enums)?.filter((item) => item === key)] : null;
}

export function getEnumObjectProp(enums, key, targetProp = 'text') {
  return getPropObject(findEnumObjectByKey(enums, key), targetProp);
}

export function getProp(items, targetId, targetProp = 'name') {
  return items?.find((element) => element?.id === targetId)?.[targetProp] || '';
}

export function getPropObject(item, targetProp) {
  return item?.[targetProp];
}

export const formatText = (text, target) => {
  return text && target ? text?.toString().replaceAll('%s', target) : '';
};

export function generateUniqueId() {
  return uuidv4();
}

export const onlyChars = /^[a-zA-Z-]*$/;

export const onlyNums = /^[0-9]*$/;

export const spaceExcluded = /^[a-zA-Z0-9\-><]+$/;

export const specialCharacters = /[!`@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/;

export const sortData = (items, targetProp) => {
  return items?.sort((a, b) => (sortCriteria(a, b, targetProp) ? 1 : -1)) || [];
};

function sortCriteria(a, b, targetProp) {
  if (targetProp) return a?.[targetProp] > b?.[targetProp];
  return a > b;
}

export function removePropFromEntity(entity, field) {
  delete entity[field];
}

export const validateNumberWithExactLength = (input, length) => {
  const regex = new RegExp(`^[0-9]{${length}}$`);
  return regex.test(input);
};

export const appendObjectInFormData = (formData, data, name) => {
  formData.append(
    name,
    new Blob([JSON.stringify(data)], {
      type: 'application/json',
    })
  );
};

export const amountToNumber = (amount) => {
  if (typeof amount === 'string') {
    let temp = amount;
    if (temp?.toString().indexOf('.')) {
      temp = temp?.toString()?.replace(/\./g, '');
    }
    temp = temp?.toString()?.replace(/,/g, '.') || 0;

    return parseFloat(temp);
  } else {
    return amount;
  }
};

export const mergeArrays = (array1, array2) => {
  return Array.from(
    new Set([...array1?.map(JSON.stringify), ...array2?.map(JSON.stringify)]),
    JSON.parse
  );
};

export const getCookieValue = (name) =>
  RegExp('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)')
    .exec(document.cookie)
    ?.pop() || '';

export const clearCookies = (cookies) =>
  cookies?.forEach((c) => {
    document.cookie = `${c}=; expires=Thu, 01 Jan 1970 00:00:00 UTC;`;
  });

export const removeAdditionalSpaces = (text) => {
  return text?.replace(/\s\s+/g, ' ').trim();
};

export const additionalInformation = (
  option,
  property,
  showAdditionalInformation
) => {
  if (showAdditionalInformation) {
    switch (property) {
      case 'user':
        switch (true) {
          case !!option.individualUserType:
            return `${option.name} - ${option.individualUserType.jmbg}`;
          case !!option.legalUserType:
            return `${option.name} - ${
              option.legalUserType.pib ||
              option.legalUserType.identificationNumber
            }`;
          case !!option.kjsUserType:
            return `${option.name} - ${option.kjsUserType.pib}`;
          case !!option.identificationNumber:
            return `${option.name} - ${option.identificationNumber}`;
          case !!option.jmbg:
            return `${option.name} - ${option.jmbg}`;
          default:
            return option.name;
        }
      case 'municipality':
      case 'cityCodebook':
      case 'populatedPlace':
        return `${option.name}`;
      default:
        return;
    }
  }
  return option.name;
  //TODO dodati po potrebi za ostale objekte
};
